import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { add, format, isBefore, sub, startOfMonth, endOfMonth } from "date-fns";
import { getOneClient, getRateToPLN, getUser } from "@/service/weexpertService";
import {
  INVOICE_TYPES,
  SUPPORTED_CURRENCIES,
  PAYMENT_NOT_COMPLETED,
  PAYMENT_METHOD_BANK_ACCOUNT,
  TRANSACTION_TYPE_LOCAL,
  ROLES
} from "@/constants";

export const MODE = {
  CREATE: 'create',
  EDIT: 'edit',
  VIEW: 'view'
};

export const getClientLabel = (client) => {
  if (!client) return '';
  return `${client.firstName} ${client.lastName}`;
};

const transformInvoiceItemsToRows = (invoiceItems) => {
  if (!invoiceItems) return [];
  return invoiceItems.map(e => ({
    id: e.id,
    name: e.name,
    GTU: e.GTU,
    pkwiu: e.pkwiu,
    quantity: e.amount,
    unit: e.unit,
    unitPriceNetto: e.priceNetto,
    priceNetto: e.priceNetto * e.amount,
    vatRate: e.typeOfVat,
    vat: e.vat * e.amount,
    priceBrutto: e.priceBrutto * e.amount,
  }));
};

export const useInvoiceForm = (invoice, mode) => {
  const isCreateMode = mode === MODE.CREATE;
  const isEditMode = mode === MODE.EDIT;
  const isViewMode = mode === MODE.VIEW;
  const isReadOnly = isViewMode;

  // User query
  const { data: userData, isLoading: isUserLoading } = useQuery({
    queryKey: 'userMe',
    queryFn: getUser,
    retry: false
  });
  const user = userData?.data;

  // Main info state
  const [client, setClient] = useState(invoice?.client ?? null);
  const [invoiceType, setInvoiceType] = useState(
    invoice ? INVOICE_TYPES[invoice.invoiceType] : INVOICE_TYPES.PROFORMA
  );
  const [issueDate, setIssueDate] = useState(
    invoice ? new Date(invoice.createdAt) : new Date()
  );
  const [domainDate, setDomainDate] = useState(
    invoice ? new Date(invoice.domainDate) : new Date()
  );
  const [status, setStatus] = useState(invoice?.state ?? "NOT_APPROVED");
  const [newInvoiceId, setNewInvoiceId] = useState(invoice?.invoiceId ?? null);

  // Counterparty state
  const [counterpartyName, setCounterpartyName] = useState(invoice?.counterpartyName ?? null);
  const [counterpartyOrigin, setCounterpartyOrigin] = useState(invoice?.counterpartyOrigin ?? null);
  const [counterpartyNip, setCounterpartyNip] = useState(invoice?.counterpartyNIP ?? null);
  const [counterpartyEmail, setCounterpartyEmail] = useState(invoice?.counterpartyEmail ?? null);
  const [counterpartyType, setCounterpartyType] = useState(invoice?.counterpartyType ?? null);
  const [counterpartyAddress, setCounterpartyAddress] = useState(invoice?.counterpartyAddress ?? null);
  const [counterpartyPostIndex, setCounterpartyPostIndex] = useState(invoice?.counterpartyPostIndex ?? null);
  const [counterpartyCity, setCounterpartyCity] = useState(invoice?.counterpartyCity ?? null);
  const [counterpartyCountry, setCounterpartyCountry] = useState(invoice?.counterpartyCountry ?? null);

  // Recipient state
  const [useDifferentRecipient, setUseDifferentRecipient] = useState(invoice ? !!invoice.recipientId : false);
  const [recipientCounterpartyName, setRecipientCounterpartyName] = useState(invoice?.recipient?.name ?? null);
  const [recipientCounterpartyOrigin, setRecipientCounterpartyOrigin] = useState(invoice?.recipient?.origin ?? null);
  const [recipientCounterpartyNip, setRecipientCounterpartyNip] = useState(invoice?.recipient?.NIP ?? null);
  const [recipientCounterpartyEmail, setRecipientCounterpartyEmail] = useState(invoice?.recipient?.email ?? null);
  const [recipientCounterpartyType, setRecipientCounterpartyType] = useState(invoice?.recipient?.counterpartyType ?? null);
  const [recipientCounterpartyAddress, setRecipientCounterpartyAddress] = useState(invoice?.recipient?.address ?? null);
  const [recipientCounterpartyPostIndex, setRecipientCounterpartyPostIndex] = useState(invoice?.recipient?.postIndex ?? null);
  const [recipientCounterpartyCity, setRecipientCounterpartyCity] = useState(invoice?.recipient?.city ?? null);
  const [recipientCounterpartyCountry, setRecipientCounterpartyCountry] = useState(invoice?.recipient?.country ?? null);

  // Payment state
  const [typeOfPayment, setTypeOfPayment] = useState(invoice?.typeOfPayment ?? TRANSACTION_TYPE_LOCAL);
  const [wayOfPayment, setWayOfPayment] = useState(invoice?.wayOfPayment ?? PAYMENT_METHOD_BANK_ACCOUNT);
  const [paymentStatus, setPaymentStatus] = useState(invoice?.paymentStatus ?? PAYMENT_NOT_COMPLETED);
  const [paymentDueDate, setPaymentDueDate] = useState(
    invoice ? new Date(invoice.paymentDueDate) : add(new Date(), { days: 7 })
  );
  const [paymentCurrency, setPaymentCurrency] = useState(invoice?.paymentCurrency ?? SUPPORTED_CURRENCIES[0].value);
  const [paymentCurrencyRate, setPaymentCurrencyRate] = useState(invoice?.paymentCurrencyRate ?? 1.00);
  const [paymentCurrencyRateDate, setPaymentCurrencyRateDate] = useState(
    invoice?.paymentCurrencyRateDate ? new Date(invoice.paymentCurrencyRateDate) : sub(new Date(), { days: 1 })
  );
  const [paymentIban, setPaymentIban] = useState(invoice?.paymentIban ?? null);
  const [paymentSwift, setPaymentSwift] = useState(invoice?.paymentSwift ?? null);
  const [bankAccounts, setBankAccounts] = useState([]);

  // Comments state
  const [internalComment, setInternalComment] = useState(invoice?.internalComment ?? '');
  const [externalComment, setExternalComment] = useState(invoice?.externalComment ?? '');

  // Positions state
  const [rows, setRows] = useState(transformInvoiceItemsToRows(invoice?.invoiceItems));

  // Summary state
  const [invoiceTotalNetto, setInvoiceTotalNetto] = useState(invoice?.invoiceTotalNetto ?? 0.00);
  const [invoiceBrutto, setInvoiceBrutto] = useState(invoice?.invoiceBrutto ?? 0.00);
  const [invoiceVAT, setInvoiceVAT] = useState(invoice?.invoiceVAT ?? 0.00);

  // Currency rate fetch effect
  useEffect(() => {
    if (isReadOnly) return;

    if (paymentCurrency === 'PLN') {
      setPaymentCurrencyRate(1.00);
      return;
    }
    getRateToPLN(paymentCurrency, format(paymentCurrencyRateDate, "yyyy-MM-dd"))
      .then(res => {
        setPaymentCurrencyRate(res.data.midRate);
      })
      .catch(e => {
        console.log(e);
      });
  }, [paymentCurrencyRateDate, paymentCurrency, isReadOnly]);

  // Client bank accounts fetch effect
  useEffect(() => {
    if (isReadOnly) return;
    if (client && client.id !== '' && user?.roles !== 'CLIENT') {
      getOneClient(+client.id)
        .then(res => {
          const incubatorAccounts = res.data.bankAccounts.filter(
            account => account.accountType === "INCUBATOR"
          );
          setBankAccounts(incubatorAccounts);
        });
    }
  }, [client, user?.roles, isReadOnly]);

  // Client role effect
  useEffect(() => {
    if (isReadOnly) return;
    if (user?.roles === 'CLIENT') {
      setClient({ id: user.id, firstName: user.firstName, lastName: user.lastName });
      const incubatorAccounts = user.bankAccounts?.filter(
        account => account.accountType === "INCUBATOR"
      ) ?? [];
      setBankAccounts(incubatorAccounts);
    }
  }, [user, isReadOnly]);

  // Totals calculation effect
  useEffect(() => {
    let netto = 0;
    let brutto = 0;
    let vat = 0;
    rows.forEach(e => {
      netto += Number(e.priceNetto) || 0;
      brutto += Number(e.priceBrutto) || 0;
      vat += Number(e.vat) || 0;
    });
    setInvoiceBrutto(brutto);
    setInvoiceTotalNetto(netto);
    setInvoiceVAT(vat);
  }, [rows]);

  // Date restrictions
  const dateOfCreationRestrictionForStaff = isEditMode
    ? [{ before: startOfMonth(issueDate), after: endOfMonth(issueDate) }]
    : [];
  const dateOfCreationRestrictionForClient = [{ before: new Date(), after: new Date() }];
  const dateOfCreationRestriction = user?.roles === ROLES.CLIENT
    ? dateOfCreationRestrictionForClient
    : dateOfCreationRestrictionForStaff;

  // Permissions
  const canChangeCounterpartyOrRecipient = isCreateMode || user?.roles !== ROLES.CLIENT;
  const isClient = user?.roles === ROLES.CLIENT;
  const isAdmin = [ROLES.ADMIN, ROLES.SUPER_ADMIN].includes(user?.roles);

  // Handler for issue date change
  const handleIssueDateChange = (date) => {
    if (!date) return;
    setPaymentDueDate(add(date, { days: 7 }));
    setIssueDate(date);
    if (isBefore(date, domainDate)) {
      setDomainDate(date);
    }
  };

  // Handler for domain date change
  const handleDomainDateChange = (date) => {
    if (!date) return;
    setDomainDate(date);
    setPaymentCurrencyRateDate(sub(date, { days: 1 }));
  };

  // Handler for counterparty selection
  const handleCounterpartySelect = (counterparty) => {
    setCounterpartyName(counterparty.name);
    setCounterpartyCountry(counterparty.country);
    setCounterpartyCity(counterparty.city);
    setCounterpartyPostIndex(counterparty.postIndex);
    setCounterpartyAddress(counterparty.address);
    setCounterpartyOrigin(counterparty.origin);
    setCounterpartyNip(counterparty.NIP);
    setCounterpartyEmail(counterparty.email);
    setCounterpartyType(counterparty.counterpartyType);
  };

  // Handler for recipient selection
  const handleRecipientSelect = (counterparty) => {
    setRecipientCounterpartyName(counterparty.name);
    setRecipientCounterpartyCountry(counterparty.country);
    setRecipientCounterpartyCity(counterparty.city);
    setRecipientCounterpartyPostIndex(counterparty.postIndex);
    setRecipientCounterpartyAddress(counterparty.address);
    setRecipientCounterpartyOrigin(counterparty.origin);
    setRecipientCounterpartyNip(counterparty.NIP);
    setRecipientCounterpartyEmail(counterparty.email);
    setRecipientCounterpartyType(counterparty.counterpartyType);
  };

  // Handler for bank account selection
  const handleBankAccountSelect = (account) => {
    setPaymentIban(account);
    const selectedAccount = bankAccounts.find(e => e.account === account);
    if (selectedAccount) {
      setPaymentSwift(selectedAccount.swift);
    }
  };

  // Get submit data
  const getSubmitData = () => {
    const recipientCounterparty = {
      counterpartyName: recipientCounterpartyName,
      counterpartyOrigin: recipientCounterpartyOrigin,
      counterpartyNip: recipientCounterpartyNip,
      counterpartyEmail: recipientCounterpartyEmail,
      counterpartyType: recipientCounterpartyType,
      counterpartyAddress: recipientCounterpartyAddress,
      counterpartyPostIndex: recipientCounterpartyPostIndex,
      counterpartyCity: recipientCounterpartyCity,
      counterpartyCountry: recipientCounterpartyCountry,
    };

    return {
      client,
      invoiceType,
      issueDate: format(issueDate, 'yyyy-MM-dd'),
      domainDate: format(domainDate, 'yyyy-MM-dd'),
      status,
      counterpartyName,
      counterpartyOrigin,
      counterpartyNip,
      counterpartyEmail,
      counterpartyType,
      counterpartyAddress,
      counterpartyPostIndex,
      counterpartyCity,
      counterpartyCountry,
      typeOfPayment,
      wayOfPayment,
      paymentStatus,
      paymentDueDate: format(paymentDueDate, 'yyyy-MM-dd'),
      paymentCurrency,
      paymentCurrencyRate,
      paymentCurrencyRateDate: format(paymentCurrencyRateDate, 'yyyy-MM-dd'),
      paymentIban,
      paymentSwift,
      internalComment,
      externalComment,
      rows,
      invoiceBrutto,
      invoiceTotalNetto,
      invoiceVAT,
      newInvoiceId: invoice && newInvoiceId !== invoice?.invoiceId ? newInvoiceId : null,
      useDifferentRecipient,
      recipientCounterparty,
    };
  };

  // Validation
  const validate = () => {
    const errors = [];
    if (!client) errors.push('client');
    if (!invoiceType) errors.push('invoiceType');
    if (!issueDate) errors.push('issueDate');
    if (!domainDate) errors.push('domainDate');
    if (!status) errors.push('status');
    if (!counterpartyName) errors.push('counterpartyName');
    if (!counterpartyType) errors.push('counterpartyType');
    if (!typeOfPayment) errors.push('typeOfPayment');
    if (useDifferentRecipient && !recipientCounterpartyName) errors.push('recipientCounterpartyName');
    if (rows.length === 0) errors.push('rows');
    if (rows.some(e =>
      !e.name ||
      !e.quantity ||
      (invoiceType !== INVOICE_TYPES.FAKTURA_KONCOWA && !e.unitPriceNetto) ||
      !e.vatRate ||
      !e.unit
    )) {
      errors.push('rowsInvalid');
    }
    return errors;
  };

  return {
    // Mode flags
    mode,
    isCreateMode,
    isEditMode,
    isViewMode,
    isReadOnly,

    // Loading state
    isLoading: isUserLoading,

    // User
    user,
    isClient,
    isAdmin,

    // Main info
    client,
    setClient,
    invoiceType,
    setInvoiceType,
    issueDate,
    handleIssueDateChange,
    domainDate,
    handleDomainDateChange,
    status,
    setStatus,
    newInvoiceId,
    setNewInvoiceId,
    dateOfCreationRestriction,

    // Counterparty
    counterpartyName,
    counterpartyOrigin,
    counterpartyNip,
    counterpartyEmail,
    counterpartyType,
    counterpartyAddress,
    counterpartyPostIndex,
    counterpartyCity,
    counterpartyCountry,
    handleCounterpartySelect,
    canChangeCounterpartyOrRecipient,

    // Recipient
    useDifferentRecipient,
    setUseDifferentRecipient,
    recipientCounterpartyName,
    recipientCounterpartyOrigin,
    recipientCounterpartyNip,
    recipientCounterpartyEmail,
    recipientCounterpartyType,
    recipientCounterpartyAddress,
    recipientCounterpartyPostIndex,
    recipientCounterpartyCity,
    recipientCounterpartyCountry,
    handleRecipientSelect,

    // Payment
    typeOfPayment,
    setTypeOfPayment,
    wayOfPayment,
    setWayOfPayment,
    paymentStatus,
    setPaymentStatus,
    paymentDueDate,
    setPaymentDueDate,
    paymentCurrency,
    setPaymentCurrency,
    paymentCurrencyRate,
    paymentCurrencyRateDate,
    setPaymentCurrencyRateDate,
    paymentIban,
    paymentSwift,
    bankAccounts,
    handleBankAccountSelect,

    // Comments
    internalComment,
    setInternalComment,
    externalComment,
    setExternalComment,

    // Positions
    rows,
    setRows,

    // Summary
    invoiceTotalNetto,
    invoiceBrutto,
    invoiceVAT,

    // Original invoice reference
    invoice,

    // Methods
    getSubmitData,
    validate,
  };
};
